import type { PipelineStep } from '@/api/alibaba';
import { PipelineTooltip } from './PipelineTooltip';
import { cn } from '@/lib/utils';

interface PipelineTimelineProps {
  steps: PipelineStep[];
  onRetry: (stepId: string) => void;
}

const SERIES_COLORS: Record<string, { bg: string; border: string; label: string }> = {
  A: { bg: 'bg-blue-50', border: 'border-blue-200', label: 'A 系列' },
  B: { bg: 'bg-orange-50', border: 'border-orange-200', label: 'B 系列' },
  C: { bg: 'bg-purple-50', border: 'border-purple-200', label: 'C 系列' },
};

function statusDot(status: PipelineStep['status']): string {
  switch (status) {
    case 'completed': return 'bg-green-500 border-green-500';
    case 'failed': return 'bg-red-500 border-red-500';
    case 'running': return 'bg-blue-500 border-blue-500 animate-pulse';
    case 'skipped': return 'border-dashed border-gray-300 bg-transparent';
    case 'pending':
    default: return 'border-gray-300 bg-transparent';
  }
}

function statusLine(fromStatus: PipelineStep['status'], toStatus: PipelineStep['status']): string {
  if (fromStatus === 'completed' && toStatus === 'completed') return 'bg-green-400';
  if (fromStatus === 'completed') return 'bg-gradient-to-r from-green-400 to-gray-300';
  return 'bg-gray-200';
}

function formatShortTime(iso: string | null): string {
  if (!iso) return '待處理';
  const d = new Date(iso);
  return d.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' });
}

// Render a single pipeline node
function PipelineNode({ step, allSteps, onRetry }: { step: PipelineStep; allSteps: PipelineStep[]; onRetry: (id: string) => void }) {
  return (
    <PipelineTooltip step={step} allSteps={allSteps} onRetry={onRetry}>
      <div className="flex flex-col items-center cursor-pointer group min-w-[72px]">
        <div
          className={cn(
            'w-5 h-5 rounded-full border-2 transition-transform group-hover:scale-125',
            statusDot(step.status),
          )}
        />
        <span className="text-[11px] mt-1 text-muted-foreground whitespace-nowrap">
          {formatShortTime(step.started_at)}
        </span>
        <span className="text-[10px] font-medium whitespace-nowrap">
          {step.step_id}
        </span>
        <span className="text-[10px] text-muted-foreground whitespace-nowrap max-w-[70px] truncate">
          {step.output_summary || step.step_name}
        </span>
      </div>
    </PipelineTooltip>
  );
}

function ConnectLine({ from, to }: { from: PipelineStep['status']; to: PipelineStep['status'] }) {
  return <div className={cn('h-[2px] w-6 flex-shrink-0', statusLine(from, to))} />;
}

// Check if C series has the forked structure (C-4 exists)
function hasCFork(seriesSteps: PipelineStep[]): boolean {
  return seriesSteps.some(s => s.step_id === 'C-4');
}

// Render C series with fork: C-1 → C-2 → [C-3 → C-5] (upper) / [C-4] (lower)
function CSeriesFork({ seriesSteps, allSteps, onRetry }: { seriesSteps: PipelineStep[]; allSteps: PipelineStep[]; onRetry: (id: string) => void }) {
  const find = (id: string) => seriesSteps.find(s => s.step_id === id);
  const c1 = find('C-1');
  const c2 = find('C-2');
  const c3 = find('C-3');
  const c4 = find('C-4');
  const c5 = find('C-5');

  return (
    <div className="flex items-center">
      {/* C-1 */}
      {c1 && <PipelineNode step={c1} allSteps={allSteps} onRetry={onRetry} />}
      {c1 && c2 && <ConnectLine from={c1.status} to={c2.status} />}
      {/* C-2 */}
      {c2 && <PipelineNode step={c2} allSteps={allSteps} onRetry={onRetry} />}
      {/* Fork after C-2 */}
      {c2 && (c3 || c4) && (
        <div className="flex flex-col items-start gap-1 ml-1">
          {/* Upper branch: C-3 → C-5 */}
          {c3 && (
            <div className="flex items-center">
              <ConnectLine from={c2.status} to={c3.status} />
              <PipelineNode step={c3} allSteps={allSteps} onRetry={onRetry} />
              {c5 && <ConnectLine from={c3.status} to={c5.status} />}
              {c5 && <PipelineNode step={c5} allSteps={allSteps} onRetry={onRetry} />}
            </div>
          )}
          {/* Lower branch: C-4 (parallel with C-3) */}
          {c4 && (
            <div className="flex items-center">
              <ConnectLine from={c2.status} to={c4.status} />
              <PipelineNode step={c4} allSteps={allSteps} onRetry={onRetry} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PipelineTimeline({ steps, onRetry }: PipelineTimelineProps) {
  // Group steps by series
  const series = ['A', 'B', 'C'];
  const grouped = series.map(s => steps.filter(st => st.series === s));

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-start gap-0 min-w-[900px] px-4 py-6">
        {series.map((s, si) => {
          const seriesSteps = grouped[si];
          const colors = SERIES_COLORS[s] || SERIES_COLORS.A;

          // C series with fork layout
          if (s === 'C' && hasCFork(seriesSteps)) {
            return (
              <div key={s} className={cn('flex items-start rounded-lg px-2 py-3', colors.bg, 'border', colors.border, si > 0 && 'ml-1')}>
                <CSeriesFork seriesSteps={seriesSteps} allSteps={steps} onRetry={onRetry} />
              </div>
            );
          }

          // Default linear layout for A/B/C (<=3 steps)
          return (
            <div key={s} className={cn('flex items-start rounded-lg px-2 py-3', colors.bg, 'border', colors.border, si > 0 && 'ml-1')}>
              {seriesSteps.map((step, i) => {
                const isLast = i === seriesSteps.length - 1 && si === series.length - 1;
                const nextStep = isLast ? null : (i < seriesSteps.length - 1 ? seriesSteps[i + 1] : grouped[si + 1]?.[0]);
                return (
                  <div key={step.step_id} className="flex items-center">
                    <PipelineNode step={step} allSteps={steps} onRetry={onRetry} />
                    {!isLast && nextStep && <ConnectLine from={step.status} to={nextStep.status} />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-4 px-4 pb-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> 完成</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> 失敗</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-500 inline-block" /> 執行中</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full border-2 border-gray-300 inline-block" /> 待處理</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full border-2 border-dashed border-gray-300 inline-block" /> 已跳過</span>
      </div>
    </div>
  );
}
